// RecorderControls — Grabador nativo del navegador para clases en vivo.
// Uso:
//   <RecorderControls classId={classId} disabled={!canModerate} />
//
// Funcionamiento:
//   1. El docente clica "Iniciar grabación".
//   2. El navegador muestra el diálogo de captura (preferCurrentTab) — el
//      docente elige compartir la pestaña actual (incluye audio del Jitsi).
//   3. Activamos un modo "limpio" en el body (.recording-clean) que oculta
//      sidebar/header para que la grabación quede enfocada en clase + pizarra.
//   4. MediaRecorder graba a WebM/Opus + VP9. Mezclamos audio del tab +
//      micrófono del docente.
//   5. Al detener, subimos el blob al backend (POST /recordings/upload con
//      multipart/form-data). Backend reenvía a Cloudinary y persiste metadata.
import { useEffect, useRef, useState } from "react";
import { Video, Square, Loader2, Mic, MicOff } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const MIME_CANDIDATES = [
  // VP9/Opus primero: códec más fiable para captura de video en Chrome/Firefox.
  // H.264 en ciertos navegadores silenciosamente descarta la pista de video,
  // resultando en archivos solo-audio. VP9 evita ese problema.
  "video/webm;codecs=vp9,opus",
  "video/webm;codecs=vp8,opus",
  "video/webm",
  "video/mp4;codecs=h264,aac",
  "video/mp4;codecs=avc1,mp4a",
  "video/mp4",
];

const AUTORECORD_KEY = "neuromath_autorecord_class";

// v5.7.1 · Registro global: permite que "Finalizar clase" (sala o mini-ventana flotante)
// detenga la grabación activa y ESPERE a que se suba antes de cerrar la sesión.
let activeRecorderApi = null;
export async function stopActiveRecordingAndUpload() {
  if (!activeRecorderApi) return false;
  try { return await activeRecorderApi.stopAndUpload(); } catch { return false; }
}

export default function RecorderControls({ classId, disabled = false, onSaved }) {
  const [state, setState] = useState("idle"); // idle | recording | uploading
  const [elapsed, setElapsed] = useState(0);
  const [includeMic] = useState(true);
  // Auto-grabar: leer la preferencia persistida del docente (default true)
  const [autoRecord, setAutoRecord] = useState(() => {
    if (disabled) return false;
    const stored = localStorage.getItem(AUTORECORD_KEY);
    return stored === null ? true : stored === "true";
  });
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const mimeRef = useRef("video/webm");
  const startTsRef = useRef(0);
  const timerRef = useRef(null);
  const displayStreamRef = useRef(null);
  const micStreamRef = useRef(null);
  const audioCtxRef = useRef(null);

  const uploadDoneRef = useRef(null);   // resolve() de la subida en curso
  const stateRef = useRef("idle");
  useEffect(() => { stateRef.current = state; }, [state]);

  // v5.7.1 · Detener y subir (para Finalizar clase). Devuelve una promesa que resuelve al terminar la subida.
  const stopAndUploadRef = useRef(null);
  stopAndUploadRef.current = () => new Promise((resolve) => {
    const rec = recorderRef.current;
    if (!rec || rec.state === "inactive") { resolve(false); return; }
    uploadDoneRef.current = resolve;
    try { rec.stop(); } catch { uploadDoneRef.current = null; resolve(false); }
  });
  useEffect(() => {
    if (disabled) return undefined;
    const api_ = { stopAndUpload: () => stopAndUploadRef.current() };
    activeRecorderApi = api_;
    return () => { if (activeRecorderApi === api_) activeRecorderApi = null; };
  }, [disabled]);

  // Al desmontar (salir de la sala) con grabación activa: detener y subir en vez de perderla
  useEffect(() => () => {
    const rec = recorderRef.current;
    if (rec && rec.state === "recording") { try { rec.stop(); } catch (_) { stopAllStreams(); } }
    else stopAllStreams();
  }, []);

  // Cerrar/recargar la pestaña con grabación activa: avisar
  useEffect(() => {
    const h = (e) => { if (stateRef.current === "recording") { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, []);

  // Persistir preferencia
  useEffect(() => {
    if (!disabled) localStorage.setItem(AUTORECORD_KEY, String(autoRecord));
  }, [autoRecord, disabled]);

  // Activa modo limpio en el body durante grabación.
  useEffect(() => {
    if (state === "recording") {
      document.body.classList.add("recording-clean");
    } else {
      document.body.classList.remove("recording-clean");
    }
  }, [state]);

  // Cronómetro
  useEffect(() => {
    if (state !== "recording") {
      clearInterval(timerRef.current);
      return;
    }
    timerRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTsRef.current) / 1000));
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [state]);

  function stopAllStreams() {
    try { displayStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch (_) { /* stream ya cerrado */ }
    try { micStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch (_) { /* stream ya cerrado */ }
    try { audioCtxRef.current?.close(); } catch (_) { /* contexto ya cerrado */ }
    displayStreamRef.current = null;
    micStreamRef.current = null;
    audioCtxRef.current = null;
  }

  function pickMime() {
    for (const m of MIME_CANDIDATES) {
      if (window.MediaRecorder && MediaRecorder.isTypeSupported(m)) return m;
    }
    return "";
  }

  async function startRecording() {
    if (state !== "idle") return;
    if (!navigator.mediaDevices?.getDisplayMedia) {
      toast.error("Tu navegador no soporta grabación de pantalla");
      return;
    }
    try {
      // Captura de pestaña (preferCurrentTab) incluyendo audio del tab (Jitsi).
      const display = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: 24 },
        audio: true,
        // Hint para Chromium 105+: preselecciona la pestaña actual.
        preferCurrentTab: true,
        selfBrowserSurface: "include",
        systemAudio: "include",
      });
      displayStreamRef.current = display;

      // Captura micrófono (siempre se intenta)
      let mic = null;
      {
        try {
          mic = await navigator.mediaDevices.getUserMedia({
            audio: { echoCancellation: true, noiseSuppression: true },
          });
          micStreamRef.current = mic;
        } catch {
          toast.warning("Sin acceso al micrófono — la grabación seguirá sin él");
        }
      }

      // Mezclar audio del tab + micro
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      audioCtxRef.current = audioCtx;
      const dest = audioCtx.createMediaStreamDestination();
      const dispAudio = display.getAudioTracks();
      if (dispAudio.length > 0) {
        audioCtx.createMediaStreamSource(new MediaStream(dispAudio)).connect(dest);
      }
      if (mic && mic.getAudioTracks().length > 0) {
        audioCtx.createMediaStreamSource(new MediaStream(mic.getAudioTracks())).connect(dest);
      }

      // Verificar que existen pistas de video antes de continuar.
      // Algunos navegadores permiten captura de pantalla que solo contiene audio.
      const videoTracks = display.getVideoTracks();
      if (videoTracks.length === 0) {
        stopAllStreams();
        toast.error("No se detectó pista de video en la captura. Intenta de nuevo seleccionando una pestaña o pantalla.");
        return;
      }

      // Stream final: video del display + audio mezclado
      const finalStream = new MediaStream([
        ...videoTracks,
        ...dest.stream.getAudioTracks(),
      ]);

      // Verificación adicional: el stream final debe tener video
      if (finalStream.getVideoTracks().length === 0) {
        stopAllStreams();
        toast.error("El stream combinado no tiene video. Verifica los permisos de captura de pantalla.");
        return;
      }

      // Si el usuario detiene la captura desde la UI del navegador, paramos.
      videoTracks.forEach((tr) => (tr.onended = () => stopRecording()));

      const mime = pickMime();
      mimeRef.current = mime || "video/webm";
      const rec = new MediaRecorder(finalStream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = onRecorderStopped;
      rec.start(2000); // chunk cada 2s
      recorderRef.current = rec;
      startTsRef.current = Date.now();
      setElapsed(0);
      setState("recording");
      toast.success("Grabación iniciada");
    } catch (e) {
      stopAllStreams();
      if (e?.name === "NotAllowedError") {
        toast.info("Cancelaste la selección de pantalla");
      } else {
        toast.error("No se pudo iniciar la grabación: " + (e?.message || "error"));
      }
    }
  }

  function stopRecording() {
    if (state !== "recording") return;
    try {
      recorderRef.current?.stop();
    } catch (_) { /* recorder ya detenido */ }
  }

  async function onRecorderStopped() {
    stopAllStreams();
    const chosen = mimeRef.current || "video/webm";
    const isMp4 = chosen.includes("mp4");
    const ext = isMp4 ? "mp4" : "webm";
    const blobType = isMp4 ? "video/mp4" : "video/webm";
    const blob = new Blob(chunksRef.current, { type: blobType });
    const dur = Math.floor((Date.now() - startTsRef.current) / 1000);
    chunksRef.current = [];
    if (blob.size < 1024) {
      setState("idle");
      toast.warning("Grabación demasiado corta — descartada");
      uploadDoneRef.current?.(false); uploadDoneRef.current = null;
      return;
    }
    setState("uploading");
    const fd = new FormData();
    fd.append("class_id", classId);
    fd.append("duration_seconds", String(dur));
    fd.append("title", `Clase ${new Date().toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" })}`);
    fd.append("file", blob, `grabacion-${classId}.${ext}`);
    try {
      const { data } = await api.post("/recordings/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        // Sin timeout: la transferencia del video puede tardar
        timeout: 0,
      });
      if (data?.processing) {
        toast.success("Grabación recibida — se está procesando y aparecerá en “Grabaciones” en unos minutos.", { duration: 6000 });
      } else {
        toast.success("Grabación guardada");
      }
      onSaved && onSaved(data.recording);
      uploadDoneRef.current?.(true); uploadDoneRef.current = null;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error("Error al subir la grabación: " + (typeof detail === "string" ? detail : "intenta de nuevo"));
      uploadDoneRef.current?.(false); uploadDoneRef.current = null;
    } finally {
      setState("idle");
    }
  }

  const fmt = (s) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}:${sec.toString().padStart(2, "0")}`;
  };

  if (disabled) return null;

  return (
    <>
    <div className="inline-flex items-center gap-1.5" data-testid="recorder-controls">
      {state === "idle" && (
        <>
          <button
            type="button"
            onClick={startRecording}
            data-testid="recorder-start"
            className="px-3 py-1.5 rounded-full bg-[#D63384] text-white text-xs font-extrabold inline-flex items-center gap-1.5 shadow-md hover:bg-[#A52258] transition-colors"
            title="Graba la pestaña actual con el audio de la reunión y tu micrófono."
          >
            <Video size={12} /> Grabar
          </button>
        </>
      )}
      {state === "recording" && (
        <>
          <span className="text-[10px] font-extrabold text-[#D63384] inline-flex items-center gap-1" data-testid="recorder-timer">
            <span className="w-2 h-2 rounded-full bg-[#D63384] animate-pulse" /> REC {fmt(elapsed)}
          </span>
          <button
            type="button"
            onClick={stopRecording}
            data-testid="recorder-stop"
            className="px-3 py-1.5 rounded-full bg-[#1F2547] text-white text-xs font-extrabold inline-flex items-center gap-1.5"
          >
            <Square size={11} /> Detener
          </button>
        </>
      )}
      {state === "uploading" && (
        <span className="text-[11px] font-bold text-[#6E7591] inline-flex items-center gap-1.5" data-testid="recorder-uploading">
          <Loader2 size={12} className="animate-spin" /> Subiendo grabación…
        </span>
      )}
    </div>
    </>
  );
}
